import { IconButton, Stack } from '@mui/material';
import { GetReportRecipientsQueryFilters } from '@schooly/api';
import {
  GroupTagSelect,
  PropertyTypeTagSelect,
  SubjectTagSelect,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { CrossIcon, TagSelect } from '@schooly/style';
import React, { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../hooks/useSchool';

type InlineFiltersTagsProps = {
  onOpenFilters: () => void;
  onResetFilters?: () => void;
  maxAmount?: number;
  filters: GetReportRecipientsQueryFilters;
};

export const InlineFiltersTags: FC<InlineFiltersTagsProps> = ({
  filters,
  onOpenFilters,
  onResetFilters,
  maxAmount = 2,
}) => {
  const { schoolId } = useSchool();
  const { formatMessage } = useIntl();
  const tags = useMemo(() => {
    const tagComponents: ReactNode[] = [];

    if (!schoolId) return tagComponents;

    const commonTagsProps = {
      size: 'small',
      onClick: onOpenFilters,
      sx: { maxWidth: 115 },
    } as const;

    for (const id of filters.subject ?? []) {
      tagComponents.push(
        <SubjectTagSelect schoolId={schoolId} key={id} id={id} {...commonTagsProps} />,
      );
    }

    if (filters.only_tutor_groups?.length) {
      tagComponents.push(
        <TagSelect
          key="tutor"
          label={formatMessage({ id: 'groups-OnlyTutorGroups' })}
          {...commonTagsProps}
        />,
      );
    }

    for (const id of filters.group ?? []) {
      tagComponents.push(<GroupTagSelect key={id} id={id} {...commonTagsProps} />);
    }

    for (const id of [...(filters.age_group ?? []), ...(filters.house ?? [])]) {
      tagComponents.push(
        <PropertyTypeTagSelect
          key={id}
          id={id}
          schoolId={schoolId}
          userRole={SchoolUserRole.Student}
          {...commonTagsProps}
        />,
      );
    }

    if (tagComponents.length > maxAmount) {
      return [
        ...tagComponents.slice(0, maxAmount),
        <TagSelect
          key="more"
          label={`+${tagComponents.length - maxAmount}`}
          {...commonTagsProps}
        />,
      ];
    }

    return tagComponents;
  }, [filters, schoolId, onOpenFilters, maxAmount, formatMessage]);

  if (!tags.length) return null;

  return (
    <Stack flexDirection="row" flexWrap="wrap" gap={1}>
      {tags}{' '}
      {onResetFilters && (
        <IconButton
          onClick={onResetFilters}
          sx={(theme) => ({
            color: theme.palette.common.grey,
            '&:hover': { color: theme.palette.text.primary },
          })}
        >
          <CrossIcon />
        </IconButton>
      )}
    </Stack>
  );
};
